import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

const ERROR_COPY: Record<string, string> = {
  bad_state: 'That sign-in attempt expired. Try again.',
  oauth_failed: 'Google sign-in did not complete. Try again.',
  unverified_email: 'That provider did not verify your email, so we could not link the account.',
  provider_not_configured: 'Google sign-in is not set up on this deployment yet.',
};

export function LoginPage({ mode }: { mode: 'login' | 'signup' }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [showEmail, setShowEmail] = useState(false);
  const [providers, setProviders] = useState<{ google: boolean; apple: boolean } | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    ERROR_COPY[params.get('error') ?? ''] ?? null,
  );

  const isSignup = mode === 'signup';

  useEffect(() => {
    api
      .authConfig()
      .then(setProviders)
      .catch(() => setProviders({ google: false, apple: false }));
  }, []);

  const anyProvider = Boolean(providers?.google || providers?.apple);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = isSignup
        ? await api.signup({ email, password, name: name || undefined })
        : await api.login({ email, password });
      await refresh();
      navigate(result.redirect ?? '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-sm px-5 py-16">
      <h1 className="font-display text-title text-ink">
        {isSignup ? 'Start a trip page' : 'Welcome back'}
      </h1>

      {anyProvider && (
        <div className="mt-7 space-y-2">
          {providers?.google && (
            <a href="/api/auth/google/start" className="btn-primary block text-center">
              Continue with Google
            </a>
          )}
          {providers?.apple && (
            <a href="/api/auth/apple/start" className="btn-quiet block w-full text-center">
              Continue with Apple
            </a>
          )}
        </div>
      )}

      {anyProvider && !showEmail ? (
        <button onClick={() => setShowEmail(true)} className="btn-quiet mt-3 w-full !text-meta">
          Or use email
        </button>
      ) : (
        <form
          onSubmit={submit}
          className={anyProvider ? 'mt-5 space-y-3 border-t border-ink/10 pt-5' : 'mt-7 space-y-3'}
        >
          {isSignup && (
            <input
              className="field"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          )}
          <input
            className="field"
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <input
            className="field"
            type="password"
            required
            minLength={8}
            placeholder="Password (8+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
          />
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
            {busy ? 'One moment…' : isSignup ? 'Create account' : 'Sign in'}
          </button>
        </form>
      )}

      {error && <p className="mt-4 font-util text-meta text-[#B4462F]">{error}</p>}

      <p className="mt-8 font-util text-meta text-muted">
        {isSignup ? 'Already have an account? ' : 'New here? '}
        <Link to={isSignup ? '/login' : '/signup'} className="text-accent underline">
          {isSignup ? 'Sign in' : 'Create one'}
        </Link>
      </p>
    </main>
  );
}
